<template>
    <div class="main-container px-2 pt-4">
        <Toast />

        <!-- Publisher Revenue Chart — top, full width -->
        <div class="mt-4">
            <PublisherDailyRevenueChart />
        </div>

        <!-- 3 stat cards -->
        <div class="grid gap-4 grid-cols-1 sm:grid-cols-3 mt-6">
            <!-- Affiliate Links awaiting -->
            <Card class="card-bg cursor-default">
                <template #content>
                    <div class="text-center">
                        <div class="font-semibold text-gray-400 text-sm mb-1">Affiliate Links Awaiting</div>
                        <div v-if="pending.links === null">
                            <Skeleton width="4rem" height="2.5rem" class="mx-auto" />
                        </div>
                        <router-link v-else :to="{ path: '/advertisers', query: { showing: 'requested' } }">
                            <div :class="pending.links === 0 ? 'no-action-required' : 'action-required'"
                                class="text-4xl font-bold underline">
                                {{ pending.links || 0 }}
                            </div>
                        </router-link>
                    </div>
                </template>
            </Card>

            <!-- Month Publishers Income -->
            <Card class="card-bg cursor-default">
                <template #content>
                    <div class="text-center">
                        <div class="font-semibold text-gray-400 text-sm mb-1">{{ currentMonth }} — Publishers Income</div>
                        <div class="text-3xl font-bold">{{ formatCurrency(revenueStats.monthNetRevenue) }}</div>
                    </div>
                </template>
            </Card>

            <!-- Month Our Income -->
            <Card class="card-bg cursor-default">
                <template #content>
                    <div class="text-center">
                        <div class="font-semibold text-gray-400 text-sm mb-1">{{ currentMonth }} — Our Income</div>
                        <div class="text-3xl font-bold">{{ formatCurrency(revenueStats.monthOurRevenue) }}</div>
                    </div>
                </template>
            </Card>
        </div>

        <!-- Top 10 tables -->
        <div class="grid gap-11 grid-cols-1 lg:grid-cols-2 mt-8">
            <div>
                <div class="flex justify-center items-center h-8 border-b-2">
                    <a class="font-bold text-xl">{{ currentMonth }}'s Top 10 Advertisers</a>
                </div>
                <DataTable :value="top10Advertisers" dataKey="advertiserId" scrollable :scrollHeight="'55vh'"
                    class="border border-gray-300 rounded-xl shadow-sm md:col-span-1 mt-2" fluid>
                    <Column field="advertiserName" header="Advertiser Name" :headerStyle="headerStyles" />
                    <Column header="Publishers Income" :headerStyle="headerStyles">
                        <template #body="{ data }">
                            {{ formatCurrency(getMetricsCustomerTotal(data)) }}
                        </template>
                    </Column>
                    <Column header="Our Income" :headerStyle="headerStyles">
                        <template #body="{ data }">
                            {{ formatCurrency(getMetricsOurTotal(data)) }}
                        </template>
                    </Column>
                </DataTable>
            </div>
            <div>
                <div class="flex justify-center items-center h-8 border-b-2">
                    <a class="font-bold text-xl">{{ currentMonth }}'s Top 10 Publishers</a>
                </div>
                <DataTable :value="top10Users" dataKey="userId" scrollable :scrollHeight="'55vh'"
                    class="border border-gray-300 rounded-xl shadow-sm md:col-span-1 mt-2" fluid>
                    <Column field="username" header="Publisher" :headerStyle="headerStyles" />
                    <Column header="Publishers Income" :headerStyle="headerStyles">
                        <template #body="{ data }">
                            {{ formatCurrency(getMetricsCustomerTotal(data)) }}
                        </template>
                    </Column>
                    <Column header="Our Income" :headerStyle="headerStyles">
                        <template #body="{ data }">
                            {{ formatCurrency(getMetricsOurTotal(data)) }}
                        </template>
                    </Column>
                </DataTable>
            </div>
        </div>
    </div>
</template>

<script setup>
import { ref, onMounted, reactive } from "vue";
import { headerStyles } from '@/global/headerStyles';
import { formatCurrency } from '@/utils/currencies';
import AdminService from "@/services/AdminService";
import { getCurrentMonth, getMonthName } from '@/utils/dateHelper';
import { getMetricsCustomerTotal, getMetricsOurTotal } from '@/utils/earnings';
import PublisherDailyRevenueChart from '@/components/earnings/PublisherDailyRevenueChart.vue';

const pending = reactive({ links: null });
const revenueStats = reactive({ monthNetRevenue: null, monthOurRevenue: null });
const top10Users = ref(null);
const top10Advertisers = ref(null);
const currentMonth = getMonthName(getCurrentMonth());

async function GetPendingApprovals() {
    try {
        const response = await AdminService.getPendingApprovals();
        pending.links = response.data.pendingSiteLinksCount;
    } catch (err) {
        console.error(err);
    }
}

async function GetTop10Users() {
    try {
        const response = await AdminService.getTopUsers();
        top10Users.value = response.data;
    } catch (err) {
        console.error(err);
    }
}

async function GetTop10Advertisers() {
    try {
        const response = await AdminService.getTopAdvertisers();
        top10Advertisers.value = response.data;
    } catch (err) {
        console.error(err);
    }
}

async function GetPlatformStats() {
    try {
        const response = await AdminService.getAdminPlatformStats();
        revenueStats.monthNetRevenue = response.data.monthNetRevenue;
        revenueStats.monthOurRevenue = response.data.monthOurRevenue;
    } catch (err) {
        console.error(err);
    }
}

onMounted(() => {
    Promise.all([
        GetPendingApprovals(),
        GetTop10Advertisers(),
        GetTop10Users(),
        GetPlatformStats()
    ]);
});
</script>

<style scoped>
.no-action-required { color: #28a745; }
.action-required { color: #dc3545; }
</style>
